import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "./Reveal";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

const CountUp = ({ from = 0, to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(from);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(from, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, from, to]);

  return (
    <span ref={ref}>
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

export const StatsBar = () => {
  const { lang } = useLang();
  const t = UI[lang].stats;

  return (
    <section data-testid="stats-bar" className="border-b border-black/10 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <Reveal>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-black/50">
            {t.eyebrow}
          </p>
        </Reveal>
        <div className="mt-10 grid gap-12 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-black/10 rtl:sm:divide-x-reverse">
          {t.items.map((s, i) => (
            <Reveal key={s.id} delay={0.1 + i * 0.12} className={i === 0 ? "" : "sm:ps-12"}>
              <div data-testid={`stat-${s.id}`}>
                {s.text ? (
                  <div className="text-4xl font-black uppercase tracking-tight text-black sm:text-5xl lg:text-6xl">
                    {s.text}
                  </div>
                ) : (
                  <div className="text-6xl font-black tracking-tighter text-black sm:text-7xl">
                    <CountUp from={s.from} to={s.to} decimals={s.decimals} suffix={s.suffix} />
                  </div>
                )}
                <p className="mt-4 max-w-[240px] text-sm leading-relaxed text-black/55">{s.caption}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
